'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectSitesList, type Filters, type PendingWorkCount } from './project_site';
import SurveyWorkflowSummary from '../reports/components/SurveyWorkflowSummary';
import { FaListUl, FaTable } from 'react-icons/fa';
import api from '../../libs/axios';
import { useAuth } from '../../provider/auth.provider';
import { useDebounce } from '../../hooks/useDebounce';
import toast from 'react-hot-toast';

interface Client {
  id: number;
  client_name: string;
  status: string;
}

interface Activity {
  id: number;
  activity_name: string;
  status: string;
}

interface Project {
  id: number;
  project_name: string;
  status: string;
  client?: Client;
  activity?: Activity;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role_id: number;
}

interface Site {
  id: number;
  site_id: string;
  project_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  assigned_engineer_id?: number;
  assigned_ta_id?: number;
  assigned_report_maker_id?: number;
  assigned_validator_id?: number;
  visit_remarks?: string;
  cancel_reason?: string;
  hold_reason?: string;
  project?: Project;
  activity?: Activity;
  site_engineer?: User;
  ta?: User;
  mis?: User;
  report_designer?: User;
  validator?: User;
  survey_workflow_status: string;
  has_pending_billing?: boolean;
}

interface SiteSortConfig {
  field: string;
  direction: 'ASC' | 'DESC';
}

interface Pagination {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

const ProjectSitesPage: React.FC = () => {
  const { user, hasPermission } = useAuth();
  // The Summary matrix is its own permission — hide the tab entirely rather
  // than let it render into a 403.
  const canViewSummary = hasPermission('Site', 'view_sites_summary');

  const [sites, setSites] = useState<Site[]>([]);
  const [pendingWorkCounts, setPendingWorkCounts] = useState<PendingWorkCount[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [users, setUsers] = useState<{ [roleId: number]: User[] }>({});
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  });
  const [sortConfig, setSortConfig] = useState<SiteSortConfig>({
    field: 'created_at',
    direction: 'DESC',
  });
  const [sitesLoading, setSitesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [advancedFilters, setAdvancedFilters] = useState<Filters>({});
  const [exportFilters, setExportFilters] = useState<Filters>({});
  // Aged cancelled / completed sites are hidden by default; the toggle (gated
  // on Site / view_aged_sites) lifts the cut-off. Kept out of `advancedFilters`
  // so clearing the filter modal cannot silently turn it back on.
  const [showAgedSites, setShowAgedSites] = useState(false);
  const [agedSitesInfo, setAgedSitesInfo] = useState<{
    hidden: boolean;
    cancelled_days: number;
    completed_days: number;
  } | null>(null);

  const navigate = useNavigate();

  const fetchSites = async (
    search = '',
    status = '',
    project_id = '',
    activity_id = '',
    page = 1,
    limitValue?: number,
    filters: Filters = advancedFilters,
    sort: SiteSortConfig = sortConfig,
    agedSites: boolean = showAgedSites,
  ) => {
    setSitesLoading(true);
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10;
      const queryParams: any = {
        page,
        limit: effectiveLimit,
        ...filters,
      };
      // Only sent when on — the server hides aged sites by default and
      // ignores the flag for callers without the permission.
      if (agedSites) queryParams.show_aged_sites = 'true';

      if (search.trim()) queryParams.search = search;
      if (status.trim()) queryParams.status = status;
      if (project_id.trim()) queryParams.project_id = project_id;
      if (activity_id.trim()) queryParams.activity_id = activity_id;
      if (sort?.field) {
        queryParams.sort_by = sort.field;
        queryParams.sort_order = sort.direction;
      }

      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key];
        } else {
          queryParams[key] = queryParams[key].toString();
        }
      });

      const response = await api.get('/survey/sites/all', {
        params: queryParams,
      });

      if (response.data.success) {
        setSites(response.data.data.sites || []);
        setPagination(response.data.data.pagination || pagination);
        setPendingWorkCounts(response.data.data.pending_work_counts || []);
        setAgedSitesInfo(response.data.data.aged_sites || null);
      }
    } catch (error) {
      console.error('Error fetching project sites:', error);
    } finally {
      setSitesLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const params: Record<string, string | number | number[]> = {
        limit: 100,
        status: 'ACTIVE',
      };
      if (user?.role?.level !== 1 && user?.id) {
        params.assigned_user_ids = [user.id];
      }
      const response = await api.get('/projects/list', { params });
      if (response.data.success) setProjects(response.data.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities/list', {
        params: { limit: 100, status: 'ACTIVE' },
      });
      if (response.data.success)
        setActivities(response.data.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchUsersByRole = async (roleId: number) => {
    try {
      const response = await api.get(`/survey/users/role/${roleId}`);
      if (response.data.success) {
        setUsers((prev) => ({
          ...prev,
          [roleId]: response.data.data.users || [],
        }));
      }
    } catch (error) {
      console.error('Error fetching users by role:', error);
    }
  };

  const updateAssignment = async (
    siteId: number,
    data: any,
    assignmentType: string,
  ) => {
    setActionLoading(true);
    try {
      const payload: any = { site_ids: [siteId] };
      switch (assignmentType) {
        case 'site_engineer':
          payload.site_engineer_userid = data.user.value;
          break;
        case 'ta':
          payload.ta_userid = data.user.value;
          break;
        case 'mis':
          payload.mis_userid = data.user.value;
          break;
        case 'report_maker':
          payload.report_designer_userid = data.user.value;
          break;
        case 'validator':
          payload.validator_userid = data.user.value;
          break;
      }
      const response = await api.put(
        '/survey/sites/update-assignment',
        payload,
      );
      if (response.data.success) {
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
      }
    } catch (error: any) {
      // A 409 here is the engineer backlog guard — the message names the sites
      // holding them up, so it has to reach the assigner, not just the console.
      console.error(`Error assigning ${assignmentType}:`, error);
      toast.error(
        error?.response?.data?.message || `Failed to assign ${assignmentType}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    // API call triggered by debouncedSearch effect below
  };

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchSites(
      debouncedSearch,
      statusFilter,
      projectFilter,
      activityFilter,
      1,
      undefined,
      advancedFilters,
      sortConfig,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    fetchSites(
      searchTerm,
      status,
      projectFilter,
      activityFilter,
      1,
      undefined,
      advancedFilters,
      sortConfig,
    );
  };

  const handleProjectFilter = (project_id: string) => {
    setProjectFilter(project_id);
    fetchSites(
      searchTerm,
      statusFilter,
      project_id,
      activityFilter,
      1,
      undefined,
      advancedFilters,
      sortConfig,
    );
  };

  const handleActivityFilter = (activity_id: string) => {
    setActivityFilter(activity_id);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activity_id,
      1,
      undefined,
      advancedFilters,
      sortConfig,
    );
  };

  const handleAdvancedFilter = (filters: Filters) => {
    setAdvancedFilters(filters);
    setExportFilters(filters);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      1,
      undefined,
      filters,
      sortConfig,
    );
  };

  const handlePageChange = (page: number) => {
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      page,
      undefined,
      advancedFilters,
      sortConfig,
    );
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      1,
      limit,
      advancedFilters,
      sortConfig,
    );
  };

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction };
    setSortConfig(sort);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      pagination.page,
      undefined,
      advancedFilters,
      sort,
    );
  };

  // The toggle refetches page 1: turning the cut-off off usually changes the
  // row count, so staying on a deep page would land on an empty one.
  const handleToggleAgedSites = (next: boolean) => {
    setShowAgedSites(next);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      1,
      undefined,
      advancedFilters,
      sortConfig,
      next,
    );
  };

  const handleAddSite = () => navigate('/sites/create');
  const handleEditSite = async () => {};

  const handleAssignEngineer = (site: Site, data: any) => {
    if (site.site_engineer) {
      updateAssignment(site.id, data, 'site_engineer');
    } else {
      api
        .post(`/survey/sites/${site.id}/assign-engineer`, data)
        .then(() => {
          fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
        })
        .catch((error: any) => {
          console.error('Error assigning engineer:', error);
          toast.error(
            error?.response?.data?.message || 'Failed to assign engineer',
          );
        });
    }
  };

  const handleAssignTA = (site: Site, data: any) =>
    updateAssignment(site.id, data, 'ta');
  const handleAssignMIS = (site: Site, data: any) =>
    updateAssignment(site.id, data, 'mis');

  const handleAssignReportMaker = (site: Site, data: any) => {
    if (site.report_designer) {
      updateAssignment(site.id, data, 'report_maker');
    } else {
      api
        .post(`/survey/sites/${site.id}/assign-report-maker`, data)
        .then(() => {
          fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
        });
    }
  };

  const handleAssignValidator = (site: Site, data: any) => {
    if (site.validator) {
      updateAssignment(site.id, data, 'validator');
    } else {
      api.post(`/survey/sites/${site.id}/assign-validator`, data).then(() => {
        fetchSites(searchTerm, statusFilter, projectFilter, activityFilter);
      });
    }
  };

  const handleCancelSite = async (site: Site, reason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/cancel`, {
        cancel_reason: reason,
      });
      if (response.data.success)
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
    } catch (error) {
      console.error('Error cancelling site:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleHoldSite = async (site: Site, reason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/hold`, {
        hold_reason: reason,
      });
      if (response.data.success)
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
    } catch (error) {
      console.error('Error putting site on hold:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSite = async (site: Site) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${site.id}/resume`);
      if (response.data.success)
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
    } catch (error) {
      console.error('Error resuming site:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAssign = async (
    siteIds: number[],
    data: any,
    type: 'reportMaker' | 'validator',
  ) => {
    setActionLoading(true);
    try {
      const payload: any = { site_ids: siteIds };
      if (type === 'reportMaker') {
        payload.report_designer_userid = data.user.value;
      } else {
        payload.validator_userid = data.user.value;
      }
      const response = await api.put(
        '/survey/sites/update-assignment',
        payload,
      );
      if (response.data.success) {
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
      }
    } catch (error: any) {
      console.error(`Error bulk assigning ${type}:`, error);
      toast.error(
        error?.response?.data?.message || `Failed to bulk assign ${type}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Bulk "Send to Client" — the list has already checked the permission and
   * that every selected site is at Report Finalized; the server re-checks both
   * and names back anything it had to skip.
   */
  const handleBulkSendToClient = async (siteIds: number[], remarks?: string) => {
    setActionLoading(true);
    try {
      const response = await api.post('/survey/sites/bulk/send-to-client', {
        site_ids: siteIds,
        ...(remarks ? { remarks } : {}),
      });
      if (response.data.success) {
        const { updated_count = 0, skipped = [] } = response.data.data || {};
        if (updated_count > 0) {
          toast.success(
            `Report sent to client for ${updated_count} site${updated_count === 1 ? '' : 's'}`,
          );
        }
        if (Array.isArray(skipped) && skipped.length > 0) {
          toast.error(
            `${skipped.length} site${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0].reason}`,
          );
        }
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
      }
    } catch (error: any) {
      console.error('Error sending reports to client:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to send reports to client',
      );
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Bulk cancel — the list only offers it for sites at Site Created and has
   * already collected the reason; the server re-checks eligibility and scope
   * and names back anything it skipped.
   */
  const handleBulkCancel = async (siteIds: number[], reason: string) => {
    setActionLoading(true);
    try {
      const response = await api.post('/survey/sites/bulk/cancel', {
        site_ids: siteIds,
        cancel_reason: reason,
      });
      if (response.data.success) {
        const { updated_count = 0, skipped = [] } = response.data.data || {};
        if (updated_count > 0) {
          toast.success(`${updated_count} site${updated_count === 1 ? '' : 's'} cancelled`);
        }
        if (Array.isArray(skipped) && skipped.length > 0) {
          toast.error(
            `${skipped.length} site${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0].reason}`,
          );
        }
        await fetchSites(
          searchTerm,
          statusFilter,
          projectFilter,
          activityFilter,
        );
      }
    } catch (error: any) {
      console.error('Error bulk cancelling sites:', error);
      toast.error(error?.response?.data?.message || 'Failed to cancel sites');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    setExportFilters(advancedFilters);
    fetchSites(
      searchTerm,
      statusFilter,
      projectFilter,
      activityFilter,
      pagination.page,
      undefined,
      advancedFilters,
      sortConfig,
    );
    fetchProjects();
    fetchActivities();
    fetchUsersByRole(2);
    fetchUsersByRole(5);
    fetchUsersByRole(6);
  }, [user?.id, user?.role?.level]);

  // 'list' = the site-by-site table; 'summary' = the stage x project matrix.
  //
  // Summary is the Survey Workflow Report's Summary view itself, mounted here:
  // the same filters and the same numbers, asked for the same way. It used to
  // be a second implementation of the idea with its own columns and its own
  // filter set, which is precisely why the two screens disagreed.
  const [viewMode, setViewMode] = useState<'list' | 'summary'>('list');

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Project Sites</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {viewMode === 'list'
              ? 'Every site, with its assignments and workflow status.'
              : 'How many sites sit at each workflow stage, per project.'}
          </p>
        </div>
        {/* Conditional rather than `hidden`: Tailwind's `flex` sets an explicit
            display, which beats the [hidden] rule from the UA stylesheet. */}
        {canViewSummary && (
        <div className="flex rounded border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            title="List — one row per site"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${viewMode === 'list' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <FaListUl className="w-4 h-4" /> List
          </button>
          <button
            type="button"
            onClick={() => setViewMode('summary')}
            aria-pressed={viewMode === 'summary'}
            title="Summary — site counts per workflow stage, one column per project"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-200 ${viewMode === 'summary' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <FaTable className="w-4 h-4" /> Summary
          </button>
        </div>
        )}
      </div>

      {viewMode === 'summary' && canViewSummary ? (
        <SurveyWorkflowSummary />
      ) : (
      <ProjectSitesList
        sites={sites}
        projects={projects}
        activities={activities}
        users={users}
        pagination={pagination}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onProjectFilter={handleProjectFilter}
        onActivityFilter={handleActivityFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSort={handleSort}
        onAddSite={handleAddSite}
        onEditSite={handleEditSite}
        onAssignEngineer={handleAssignEngineer}
        onAssignTA={handleAssignTA}
        onAssignMIS={handleAssignMIS}
        onAssignReportMaker={handleAssignReportMaker}
        onAssignValidator={handleAssignValidator}
        onCancelSite={handleCancelSite}
        onHoldSite={handleHoldSite}
        onResumeSite={handleResumeSite}
        onAdvancedFilter={handleAdvancedFilter}
        onBulkAssign={handleBulkAssign}
        loading={sitesLoading}
        actionLoading={actionLoading}
        exportFilters={exportFilters}
        exportSort={sortConfig}
        pendingWorkCounts={pendingWorkCounts}
        pendingWorkTitle="Pending Work"
        onBulkSendToClient={handleBulkSendToClient}
        onBulkCancel={handleBulkCancel}
        showAgedSites={showAgedSites}
        onToggleAgedSites={handleToggleAgedSites}
        agedSitesInfo={agedSitesInfo}
      />
      )}
    </div>
  );
};

export default ProjectSitesPage;
